// src/pages/admin/Approvals.tsx
//
// Approvals queue. Lists pending items and lets the admin approve
// or reject them.
//
// Day 5 polish:
//   - Rejection now requires a free-text reason that's saved on the item
//   - Every decision is appended to the audit log (visible on the dashboard)
//   - Decided items (approved / rejected) are kept around so the
//     admin can revisit decisions; their status is shown inline
//   - Items are filtered by status (pending | approved | rejected)
//
// Day 6 polish:
//   - Subscribed to the Agent Balloon bus: brand-search candidates
//     show up here as drafts and can be approved/rejected like any
//     other submission. See handleAgentDraft() below.

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check,
  Filter,
  ShieldCheck,
  X,
  ExternalLink,
  AlertTriangle,
  Clock,
  XCircle,
  CheckCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardContent } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import {
  DEMO_APPROVALS,
  DEMO_PRODUCTS,
  type ApprovalItem,
  type Product,
  type AuditEntry,
  type Category,
} from "@/lib/demoData";
import { useAuth } from "@/components/auth/AuthContext";
import { subscribeAgent } from "@/lib/agent/agentBus";
import { cn } from "@/lib/cn";

// Categories the agent is allowed to mint. Anything outside this set
// falls back to "accessories" so the strict `Category` union still
// type-checks. Real catalog data comes from DEMO_PRODUCTS.
const AGENT_CATEGORIES = new Set<Category>([
  "video",
  "audio",
  "screens",
  "acoustic",
  "accessories",
]);

function asCategory(input: string | undefined): Category {
  if (input && AGENT_CATEGORIES.has(input as Category)) {
    return input as Category;
  }
  return "accessories";
}

type Filter = "pending" | "approved" | "rejected" | "all";

/** i18n key for each filter chip label; the filter VALUE stays untouched. */
const FILTER_LABEL_KEYS: Record<Filter, string> = {
  pending: "admin.approvals.filterPending",
  approved: "admin.approvals.filterApproved",
  rejected: "admin.approvals.filterRejected",
  all: "admin.approvals.filterAll",
};

/** i18n key for each approval item type badge. */
const ITEM_TYPE_LABEL_KEYS: Record<string, string> = {
  "product-create": "admin.approvals.typeProductCreate",
  "product-update": "admin.approvals.typeProductUpdate",
  "price-update": "admin.approvals.typePriceUpdate",
};

/** i18n key for each audit action badge. */
const AUDIT_ACTION_LABEL_KEYS: Record<string, string> = {
  approve: "admin.approvals.actionApprove",
  reject: "admin.approvals.actionReject",
  "approve-product-draft": "admin.approvals.actionApproveProductDraft",
  "import-from-agent": "admin.approvals.actionImportFromAgent",
};

/** Find a matching pending product draft (the row that came from the agent). */
function findPendingProduct(sku: string): Product | undefined {
  return DEMO_PRODUCTS.find((p) => p.sku === sku && p.approved === "pending");
}

export function AdminApprovals() {
  const auth = useAuth();
  const { t } = useTranslation();
  const [filter, setFilter] = useState<Filter>("pending");
  const [items, setItems] = useState<ApprovalItem[]>(DEMO_APPROVALS);
  const [pendingProducts, setPendingProducts] = useState<Product[]>(
    DEMO_PRODUCTS.filter((p) => p.approved === "pending"),
  );
  const [auditLog, setAuditLog] = useState<AuditEntry[]>([]);
  // Local state for the rejection form (open + typed reason)
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");

  const filtered = items.filter((it) => {
    if (filter === "all") return true;
    if (filter === "pending") return !it.decidedAt;
    if (filter === "approved") return !!it.decidedAt && !it.rejectionReason;
    return !!it.rejectionReason;
  });

  const pending = items.filter((i) => !i.decidedAt).length;
  const approvedCount = items.filter((i) => i.decidedAt && !i.rejectionReason).length;
  const rejectedCount = items.filter((i) => !!i.rejectionReason).length;

  function recordAudit(action: AuditEntry["action"], sku: string | undefined, summary: string) {
    const entry: AuditEntry = {
      id: `EVT-${String(auditLog.length + 1).padStart(3, "0")}`,
      ts: new Date().toISOString(),
      actor: auth.user?.username || auth.user?.email || "admin",
      action,
      sku,
      summary,
    };
    setAuditLog((prev) => [entry, ...prev]);
  }

  function approve(id: string) {
    const item = items.find((i) => i.id === id);
    if (!item) return;
    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? { ...it, decidedAt: new Date().toISOString(), decidedBy: auth.user?.username || auth.user?.email || "admin" }
          : it,
      ),
    );
    setPendingProducts((prev) => prev.filter((p) => p.sku !== item.sku));
    if (item.sku) {
      recordAudit("approve", item.sku, t("admin.approvals.auditApproved", { title: item.title }));
    }
  }

  function confirmReject() {
    if (!rejectingId) return;
    const reason = rejectReason.trim();
    if (reason.length < 5) return;
    const item = items.find((i) => i.id === rejectingId);
    if (!item) return;
    setItems((prev) =>
      prev.map((it) =>
        it.id === rejectingId
          ? {
              ...it,
              decidedAt: new Date().toISOString(),
              decidedBy: auth.user?.username || auth.user?.email || "admin",
              rejectionReason: reason,
            }
          : it,
      ),
    );
    setPendingProducts((prev) => prev.filter((p) => p.sku !== item.sku));
    recordAudit(
      "reject",
      item.sku,
      t("admin.approvals.auditRejected", { title: item.title, reason }),
    );
    setRejectingId(null);
    setRejectReason("");
  }

  // === Agent bus ===
  // The floating Agent Balloon emits `draft:product` events when the
  // user asks it to import brand-search candidates. We surface them
  // here so the admin can approve/reject them alongside regular
  // submissions. Duplicates (same SKU) are silently de-duplicated.
  useEffect(() => {
    return subscribeAgent((e) => {
      if (e.type !== "draft:product") return;
      const draft = e.payload;
      const newProduct: Product = {
        sku: draft.sku,
        name: draft.name,
        brand: draft.brand,
        category: asCategory(draft.category),
        description: draft.summary,
        priceEUR: draft.priceEUR ?? 0,
        currency: "EUR",
        image: draft.image ?? "",
        images: [],
        specs: [],
        tags: ["agent-import", draft.brand.toLowerCase()],
        inStock: false,
        featured: false,
        updatedAt: new Date().toISOString(),
        approved: "pending",
      };
      setPendingProducts((prev) => {
        if (prev.some((p) => p.sku === newProduct.sku)) return prev;
        return [newProduct, ...prev];
      });
      recordAudit(
        "import-from-agent",
        newProduct.sku,
        t("admin.approvals.auditAgentImport", {
          name: newProduct.name,
          brand: newProduct.brand,
        }),
      );
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-510 tracking-tight text-fg-primary">
            {t("admin.approvals.title")}
          </h1>
          <p className="mt-1 text-sm text-fg-tertiary">
            {t("admin.approvals.stats", {
              pending,
              approved: approvedCount,
              rejected: rejectedCount,
            })}{" "}
            {pendingProducts.length > 0 && (
              <span>· {t("admin.approvals.productDrafts", { count: pendingProducts.length })}</span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(["pending", "approved", "rejected", "all"] as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              aria-pressed={filter === f}
              className={cn(
                "rounded px-2 py-1 text-xs font-510 uppercase tracking-wider transition-colors",
                filter === f
                  ? "bg-benchmark-teal/20 text-benchmark-teal-bright"
                  : "bg-bg-elevated text-fg-secondary hover:bg-bg-hover hover:text-fg-primary",
              )}
            >
              {t(FILTER_LABEL_KEYS[f])}
            </button>
          ))}
        </div>
      </header>

      {/* Pending product drafts (created by agents or manual UI) */}
      {filter === "pending" && pendingProducts.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h2 className="mb-3 text-xs font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.approvals.pendingDraftsTitle", { count: pendingProducts.length })}
            </h2>
            <div className="space-y-3">
              {pendingProducts.map((p) => (
                <div
                  key={p.sku}
                  className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-bg-canvas p-3"
                >
                  <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded bg-bg-elevated">
                    <img src={p.image} alt="" className="h-full w-full object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <code className="rounded bg-bg-elevated px-1.5 py-0.5 font-mono text-[10px] text-fg-secondary">
                        {p.sku}
                      </code>
                      <Badge variant="warning" size="sm">
                        {t("admin.approvals.pendingReview")}
                      </Badge>
                      <span className="text-[10px] text-fg-quaternary">{p.brand}</span>
                    </div>
                    <div className="mt-0.5 truncate text-sm font-510 text-fg-primary">
                      {p.name}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => {
                      setPendingProducts((prev) => prev.filter((x) => x.sku !== p.sku));
                      recordAudit(
                        "approve-product-draft",
                        p.sku,
                        t("admin.approvals.auditApprovedDraft", { sku: p.sku }),
                      );
                    }}
                  >
                    <Check className="h-3 w-3" strokeWidth={1.5} />
                    {t("admin.approvals.approve")}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setPendingProducts((prev) => prev.filter((x) => x.sku !== p.sku));
                      recordAudit(
                        "reject",
                        p.sku,
                        t("admin.approvals.auditRejectedDraft", { sku: p.sku }),
                      );
                    }}
                  >
                    <X className="h-3 w-3" strokeWidth={1.5} />
                    {t("admin.approvals.reject")}
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {items.length === 0 ? (
        <EmptyState
          title={t("admin.approvals.queueClear")}
          description={t("admin.approvals.queueClearDescription")}
          icon={<ShieldCheck className="h-5 w-5 text-accent-green" strokeWidth={1.5} />}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          title={t("admin.approvals.noItemsMatch")}
          description={t("admin.approvals.noItemsMatchDescription", {
            status: t(FILTER_LABEL_KEYS[filter]),
          })}
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((item) => (
            <Card key={item.id}>
              <CardContent className="p-5">
                <ApprovalRow
                  item={item}
                  pending={findPendingProduct(item.sku)}
                  currentActor={auth.user?.username || auth.user?.email || "admin"}
                  onApprove={() => approve(item.id)}
                  onStartReject={() => {
                    setRejectingId(item.id);
                    setRejectReason("");
                  }}
                  onCancelReject={() => {
                    setRejectingId(null);
                    setRejectReason("");
                  }}
                  onConfirmReject={confirmReject}
                  rejectReason={rejectingId === item.id ? rejectReason : ""}
                  onChangeReject={setRejectReason}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Activity log (audit trail) */}
      {auditLog.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <h2 className="mb-3 text-xs font-510 uppercase tracking-wider text-fg-tertiary">
              {t("admin.approvals.sessionActivity", { count: auditLog.length })}
            </h2>
            <ul className="divide-y divide-border/50">
              {auditLog.map((entry) => {
                const actionKey = AUDIT_ACTION_LABEL_KEYS[entry.action];
                const actionLabel = actionKey ? t(actionKey) : entry.action;
                return (
                <li key={entry.id} className="py-2 text-xs">
                  <div className="flex items-baseline gap-2">
                    <span className="font-mono text-[10px] text-fg-quaternary">{entry.id}</span>
                    <Badge
                      variant={
                        entry.action.startsWith("approve") ? "success" : entry.action.startsWith("reject") ? "danger" : "default"
                      }
                      size="sm"
                    >
                      {actionLabel}
                    </Badge>
                    {entry.sku && (
                      <code className="rounded bg-bg-elevated px-1 py-0.5 text-[10px] text-fg-secondary">
                        {entry.sku}
                      </code>
                    )}
                  </div>
                  <div className="mt-0.5 text-fg-secondary">{entry.summary}</div>
                  <div className="mt-0.5 text-fg-quaternary">
                    {t("admin.approvals.auditBy", { actor: entry.actor })} ·{" "}
                    {new Date(entry.ts).toLocaleString()}
                  </div>
                </li>
                );
              })}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

interface ApprovalRowProps {
  item: ApprovalItem;
  pending?: Product | undefined;
  currentActor: string;
  isThisBeingRejected?: boolean | undefined;
  onApprove: () => void;
  onStartReject: () => void;
  onCancelReject: () => void;
  onConfirmReject: () => void;
  rejectReason: string;
  onChangeReject: (v: string) => void;
}
function ApprovalRow({
  item,
  pending,
  isThisBeingRejected = false,
  onApprove,
  onStartReject,
  onCancelReject,
  onConfirmReject,
  rejectReason,
  onChangeReject,
}: ApprovalRowProps) {
  const { t } = useTranslation();
  const decided = !!item.decidedAt;
  const rejected = !!item.rejectionReason;
  const typeKey = ITEM_TYPE_LABEL_KEYS[item.type];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <RiskBadge risk={item.risk} />
            <Badge variant="default" size="sm">
              {typeKey ? t(typeKey) : item.type.replace("-", " ")}
            </Badge>
            <span className="font-mono text-[10px] text-fg-quaternary">{item.id}</span>
            <code className="rounded bg-bg-elevated px-1.5 py-0.5 text-[10px] text-fg-secondary">
              {item.sku}
            </code>
            {decided && (
              <Badge variant={rejected ? "danger" : "success"} size="sm">
                {t(rejected ? "admin.approvals.statusRejected" : "admin.approvals.statusApproved")}
              </Badge>
            )}
            {pending && (
              <Badge variant="warning" size="sm">
                {t("admin.approvals.hasDraft")}
              </Badge>
            )}
          </div>
          <h3 className="mt-2 text-base font-510 tracking-tight text-fg-primary">
            {item.title}
          </h3>
        </div>
      </div>

      <p className="text-sm text-fg-secondary">{item.summary}</p>

      {pending && (
        <div className="rounded-md border border-accent-amber/30 bg-accent-amber/5 p-3">
          <div className="mb-2 text-xs font-510 uppercase tracking-wider text-accent-amber">
            {t("admin.approvals.willPublish")}
          </div>
          <div className="flex items-center gap-3">
            <img src={pending.image} alt="" className="h-12 w-16 rounded object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
            <div className="flex-1">
              <div className="text-sm font-510 text-fg-primary">{pending.name}</div>
              <div className="mt-0.5 line-clamp-1 text-xs text-fg-tertiary">
                {pending.description}
              </div>
            </div>
            <div className="text-right text-xs">
              <div className="font-510 text-fg-primary">
                EUR {pending.priceEUR.toLocaleString()}
              </div>
              <div className="mt-0.5 text-[10px] uppercase tracking-wider text-fg-tertiary">
                {pending.brand} · {pending.category}
              </div>
            </div>
          </div>
        </div>
      )}

      {rejected && item.rejectionReason && (
        <div className="rounded-md border border-accent-rose/30 bg-accent-rose/5 p-3">
          <div className="text-xs font-510 uppercase tracking-wider text-accent-rose">
            {t("admin.approvals.rejectionReason")}
          </div>
          <p className="mt-1 text-sm text-fg-primary">{item.rejectionReason}</p>
        </div>
      )}

      {item.decidedAt && (
        <div className="text-xs text-fg-quaternary">
          {t("admin.approvals.decidedBy", {
            date: new Date(item.decidedAt).toLocaleString(),
            actor: item.decidedBy,
          })}
        </div>
      )}

      <div className="border-t border-border pt-3">
        {!decided && (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-fg-tertiary">
              <span>{t("admin.approvals.submittedBy")} </span>
              <span className="font-mono text-fg-secondary">{item.submittedBy}</span>
              <span> · {new Date(item.submittedAt).toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" href="#"
                onClick={(e) => {
                  e.preventDefault();
                  window.alert(t("admin.approvals.diffAlert", { sku: item.sku }));
                }}
              >
                <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.5} />
                {t("admin.approvals.viewDiff")}
              </Button>
              {!isThisBeingRejected && (
                <Button variant="ghost" size="sm" onClick={onStartReject}>
                  <X className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {t("admin.approvals.reject")}
                </Button>
              )}
              <Button size="sm" onClick={onApprove}>
                <Check className="h-3.5 w-3.5" strokeWidth={1.5} />
                {t("admin.approvals.approve")}
              </Button>
            </div>
          </div>
        )}

        {/* Reject-reason form (inline) */}
        {isThisBeingRejected && (
          <div className="mt-3 space-y-2">
            <label className="block">
              <span className="mb-1 block text-xs font-510 uppercase tracking-wider text-fg-tertiary">
                {t("admin.approvals.rejectionReasonRequired")}
              </span>
              <textarea
                value={rejectReason}
                onChange={(e) => onChangeReject(e.target.value)}
                rows={2}
                placeholder={t("admin.approvals.rejectionReasonPlaceholder")}
                className="w-full rounded border border-border bg-bg-canvas px-3 py-1.5 text-sm text-fg-primary placeholder:text-fg-quaternary"
              />
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={onCancelReject}>
                {t("admin.approvals.cancel")}
              </Button>
              <Button
                size="sm"
                variant="danger"
                disabled={rejectReason.trim().length < 5}
                onClick={onConfirmReject}
              >
                <XCircle className="h-3 w-3" strokeWidth={1.5} />
                {t("admin.approvals.rejectWithReason")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RiskBadge({ risk }: { risk: "low" | "medium" | "high" }) {
  const { t } = useTranslation();
  if (risk === "high") {
    return (
      <Badge variant="danger" size="sm">
        <AlertTriangle className="h-3 w-3" strokeWidth={1.5} />
        {t("admin.approvals.riskHigh")}
      </Badge>
    );
  }
  if (risk === "medium") {
    return <Badge variant="warning" size="sm">{t("admin.approvals.riskMedium")}</Badge>;
  }
  return (
    <Badge variant="success" size="sm">
      <CheckCircle className="h-3 w-3" strokeWidth={1.5} />
      {t("admin.approvals.riskLow")}
    </Badge>
  );
}

// keep imports referenced
void Clock;
